import React from 'react';
import './skeleton.css';

type SkeletonProps = {
  width?: number | string;
  height?: number | string;
  borderRadius?: number | string;
  className?: string;
  style?: React.CSSProperties;
};

/** Single shimmer block — reuse across pages. */
export const Skeleton: React.FC<SkeletonProps> = ({
  width = '100%',
  height = 14,
  borderRadius = 6,
  className = '',
  style,
}) => (
  <span
    className={`sk ${className}`.trim()}
    style={{
      width,
      height,
      borderRadius,
      ...style,
    }}
    aria-hidden
  />
);

type TableBodySkeletonProps = {
  rows?: number;
  cols?: number;
};

/**
 * Data-only table body skeleton — keep real thead / filters / title.
 * Use inside <tbody>.
 */
export const TableBodySkeleton: React.FC<TableBodySkeletonProps> = ({
  rows = 8,
  cols = 6,
}) => (
  <>
    {Array.from({ length: rows }).map((_, r) => (
      <tr key={r} className="sk-table-row">
        {Array.from({ length: cols }).map((_, c) => (
          <td key={c}>
            <Skeleton
              width={
                c === 0
                  ? '72%'
                  : c === cols - 1
                    ? 36
                    : `${46 + ((r + c) % 5) * 8}%`
              }
              height={c === 0 ? 15 : 13}
            />
          </td>
        ))}
      </tr>
    ))}
  </>
);

/** Alias used by sites publisher table. */
export const SitesTableSkeleton = TableBodySkeleton;

type SitesCardSkeletonProps = {
  count?: number;
};

const SITE_METRICS: { label: string; source?: string; color: string; wide?: boolean }[] = [
  { label: 'DR', source: '(Ahrefs)', color: '#0369a1' },
  { label: 'Traffic', source: '(Ahrefs)', color: '#059669', wide: true },
  { label: 'Spam Score', color: '#d97706' },
  { label: 'Domain Age', color: '#7c3aed', wide: true },
];

/** Site list data skeleton only — same card structure as a loaded site. Title and filters stay real. */
export const SitesCardSkeleton: React.FC<SitesCardSkeletonProps> = ({ count = 6 }) => (
  <div role="status" aria-busy="true" aria-label="Loading sites">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="es-site-card">
        <div className="es-site-top">
          <div className="es-site-fav" aria-hidden />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Skeleton width={`${28 + (i % 3) * 10}%`} height={17} />
            <div className="es-site-cats">
              <Skeleton width={72} height={18} borderRadius={4} />
              <Skeleton width={96} height={18} borderRadius={4} />
              <Skeleton width={64} height={18} borderRadius={4} />
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <div style={{ textAlign: 'right' }}>
              <Skeleton width={44} height={17} />
              <Skeleton width={58} height={12} style={{ marginTop: 4 }} />
            </div>
            <Skeleton width={78} height={36} borderRadius={7} />
          </div>
        </div>
        <div className="es-metrics">
          {SITE_METRICS.map((metric) => (
            <div key={metric.label} className="es-metric-box">
              <div className="es-metric-label" style={{ color: metric.color }}>
                {metric.label}
                {metric.source ? <span className="es-metric-source"> {metric.source}</span> : null}
              </div>
              <Skeleton width={metric.wide ? 52 : 28} height={16} />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

type CardListSkeletonProps = {
  count?: number;
  /** denser = shorter cards for simple lists */
  dense?: boolean;
};

/** Generic card/list data skeleton (planning, billing orders, etc.). */
export const CardListSkeleton: React.FC<CardListSkeletonProps> = ({
  count = 5,
  dense = false,
}) => (
  <div className="sk-card-list" role="status" aria-busy="true" aria-label="Loading">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className={`sk-list-card${dense ? ' sk-list-card--dense' : ''}`}>
        <div className="sk-list-card-top">
          <div className="sk-list-card-meta">
            <Skeleton width={`${40 + (i % 3) * 12}%`} height={16} />
            <Skeleton width={`${28 + (i % 2) * 10}%`} height={12} />
          </div>
          <Skeleton width={72} height={28} borderRadius={8} />
        </div>
        {!dense && (
          <div className="sk-list-card-bottom">
            <Skeleton width="30%" height={12} />
            <Skeleton width="22%" height={12} />
            <Skeleton width="18%" height={12} />
          </div>
        )}
      </div>
    ))}
  </div>
);

type OrderBunchSkeletonProps = {
  count?: number;
  rowsPerBunch?: number;
};

/** Orders / placements bunch cards with nested row placeholders. */
export const OrderBunchSkeleton: React.FC<OrderBunchSkeletonProps> = ({
  count = 3,
  rowsPerBunch = 3,
}) => (
  <div className="sk-bunch-list" role="status" aria-busy="true" aria-label="Loading orders">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="sk-bunch-card">
        <div className="sk-bunch-header">
          <div className="sk-bunch-header-left">
            <Skeleton width={120} height={16} />
            <Skeleton width={80} height={20} borderRadius={999} />
          </div>
          <Skeleton width={96} height={14} />
        </div>
        <div className="sk-bunch-rows">
          {Array.from({ length: rowsPerBunch }).map((_, r) => (
            <div key={r} className="sk-bunch-row">
              <Skeleton width={28} height={28} borderRadius={8} />
              <Skeleton width={`${35 + ((i + r) % 3) * 10}%`} height={13} />
              <Skeleton width={48} height={13} />
              <Skeleton width={56} height={22} borderRadius={6} />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

type DashboardSkeletonProps = {
  statCount?: number;
  listRows?: number;
};

/** Dashboard data area: stat cards + activity/list blocks. */
export const DashboardSkeleton: React.FC<DashboardSkeletonProps> = ({
  statCount = 4,
  listRows = 5,
}) => (
  <div className="sk-dashboard" role="status" aria-busy="true" aria-label="Loading dashboard">
    <div className="sk-dashboard-stats">
      {Array.from({ length: statCount }).map((_, i) => (
        <div key={i} className="sk-stat-card">
          <Skeleton width="40%" height={12} />
          <Skeleton width="55%" height={28} />
          <Skeleton width="30%" height={11} />
        </div>
      ))}
    </div>
    <div className="sk-dashboard-panels">
      <div className="sk-dashboard-panel">
        <Skeleton width={140} height={16} style={{ marginBottom: 14 }} />
        {Array.from({ length: listRows }).map((_, i) => (
          <div key={i} className="sk-dashboard-row">
            <Skeleton width={32} height={32} borderRadius="50%" />
            <div style={{ flex: 1 }}>
              <Skeleton width="60%" height={13} />
              <Skeleton width="40%" height={11} style={{ marginTop: 6 }} />
            </div>
            <Skeleton width={48} height={13} />
          </div>
        ))}
      </div>
      <div className="sk-dashboard-panel">
        <Skeleton width={120} height={16} style={{ marginBottom: 14 }} />
        <Skeleton width="100%" height={160} borderRadius={10} />
      </div>
    </div>
  </div>
);

type FormSkeletonProps = {
  fields?: number;
  /** Two-column grid when space allows */
  columns?: 1 | 2;
};

/** Settings / form data skeleton — keep page chrome (back button, title) real. */
export const FormSkeleton: React.FC<FormSkeletonProps> = ({
  fields = 6,
  columns = 2,
}) => (
  <div
    className={`sk-form${columns === 2 ? ' sk-form--2col' : ''}`}
    role="status"
    aria-busy="true"
    aria-label="Loading form"
  >
    {Array.from({ length: fields }).map((_, i) => (
      <div key={i} className="sk-form-field">
        <Skeleton width={`${28 + (i % 3) * 8}%`} height={11} />
        <Skeleton width="100%" height={40} borderRadius={8} />
      </div>
    ))}
  </div>
);

type FeedSkeletonProps = {
  count?: number;
};

/** Audit / activity feed data skeleton. */
export const FeedSkeleton: React.FC<FeedSkeletonProps> = ({ count = 6 }) => (
  <div className="sk-feed" role="status" aria-busy="true" aria-label="Loading activity">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="sk-feed-row">
        <Skeleton width={36} height={36} borderRadius="50%" />
        <div className="sk-feed-meta">
          <Skeleton width={`${48 + (i % 4) * 8}%`} height={14} />
          <Skeleton width={`${32 + (i % 3) * 10}%`} height={11} style={{ marginTop: 6 }} />
        </div>
        <Skeleton width={72} height={12} />
      </div>
    ))}
  </div>
);

/** Menu switch: show while the next page’s code is still downloading. */
export const RoutePageSkeleton: React.FC<{
  variant?: 'list' | 'dashboard' | 'sites' | 'orders' | 'form' | 'feed';
}> = ({ variant = 'list' }) => {
  if (variant === 'dashboard') return <DashboardSkeleton />;
  if (variant === 'sites') return <SitesCardSkeleton count={6} />;
  if (variant === 'orders') return <OrderBunchSkeleton />;
  if (variant === 'form') return <FormSkeleton />;
  if (variant === 'feed') return <FeedSkeleton />;
  return <CardListSkeleton count={6} />;
};
